import React, { useEffect, useState } from 'react';
import settingsService from '../../services/settingsService';
import { organizationsService } from '../../services/organizationsService';

const IntegrationSettings = () => {
  const [organizations, setOrganizations] = useState([]);
  const [selectedOrgId, setSelectedOrgId] = useState('');
  const [loadingOrgs, setLoadingOrgs] = useState(false);
  const [loadingConfig, setLoadingConfig] = useState(false);
  const [rotating, setRotating] = useState(false);
  const [orgPickerValue, setOrgPickerValue] = useState('');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [config, setConfig] = useState(null);
  const [newApiKey, setNewApiKey] = useState('');
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const [listLoading, setListLoading] = useState(false);
  const [credentialRows, setCredentialRows] = useState([]);
  const [pagination, setPagination] = useState({ page: 1, limit: 10, total: 0, total_pages: 0 });

  const organizationOptions = organizations.map((org) => ({
    id: String(org.id),
    label: `${org.name} (#${org.id})`
  }));

  useEffect(() => {
    const loadOrganizations = async () => {
      try {
        setLoadingOrgs(true);
        setError('');
        const data = await organizationsService.getOrganizationsList();
        const list = Array.isArray(data) ? data : [];
        setOrganizations(list);
      } catch (e) {
        setError(e?.message || 'Failed to load organizations');
      } finally {
        setLoadingOrgs(false);
      }
    };
    loadOrganizations();
  }, []);

  const loadCredentialRows = async (nextPage = 1, nextSearch = search) => {
    try {
      setListLoading(true);
      const response = await settingsService.getIntegrationApiKeysList({
        page: nextPage,
        limit: 10,
        search: nextSearch
      });
      const data = response?.data || response || {};
      setCredentialRows(Array.isArray(data.items) ? data.items : []);
      setPagination(data.pagination || { page: nextPage, limit: 10, total: 0, total_pages: 0 });
    } catch (e) {
      setError(e?.response?.data?.message || e?.message || 'Failed to load integration credentials list');
    } finally {
      setListLoading(false);
    }
  };

  useEffect(() => {
    loadCredentialRows(1, '');
  }, []);

  const fetchConfig = async (orgId) => {
    if (!orgId) return;
    try {
      setLoadingConfig(true);
      setError('');
      setSuccess('');
      const response = await settingsService.getIntegrationApiKeyConfig(orgId);
      setConfig(response?.data || response || null);
    } catch (e) {
      setError(e?.response?.data?.message || e?.message || 'Failed to fetch integration config');
      setConfig(null);
    } finally {
      setLoadingConfig(false);
    }
  };

  const handleOrgChange = async (orgId) => {
    setSelectedOrgId(orgId);
    setNewApiKey('');
    await fetchConfig(orgId);
  };

  const handleOrgPickerChange = async (e) => {
    const value = e.target.value;
    setOrgPickerValue(value);
    const matched = organizationOptions.find((opt) => opt.label === value);
    if (matched) {
      await handleOrgChange(matched.id);
    }
  };

  const handleRotateKey = async () => {
    if (!selectedOrgId) {
      setError('Please select an organization first');
      return;
    }
    try {
      setRotating(true);
      setError('');
      setSuccess('');
      const response = await settingsService.rotateIntegrationApiKey(Number(selectedOrgId), 1);
      const data = response?.data || response || {};
      setNewApiKey(data.api_key || '');
      setSuccess('Integration API key rotated successfully. Save the key now; it will not be shown again.');
      await fetchConfig(selectedOrgId);
      await loadCredentialRows(page, search);
    } catch (e) {
      setError(e?.response?.data?.message || e?.message || 'Failed to rotate integration API key');
    } finally {
      setRotating(false);
    }
  };

  const copyApiKey = async () => {
    if (!newApiKey) return;
    try {
      if (navigator?.clipboard?.writeText) {
        await navigator.clipboard.writeText(newApiKey);
      } else {
        const el = document.createElement('textarea');
        el.value = newApiKey;
        el.setAttribute('readonly', '');
        el.style.position = 'absolute';
        el.style.left = '-9999px';
        document.body.appendChild(el);
        el.select();
        document.execCommand('copy');
        document.body.removeChild(el);
      }
      setSuccess('API key copied to clipboard.');
    } catch (e) {
      setError('Unable to copy automatically. Please copy manually from the key box.');
    }
  };

  return (
    <div className="integration-settings">
      <div className="integration-card">
        <div className="card-header">
          <h3><i className="fas fa-plug"></i> Integrations</h3>
          <p>Manage attendance API credentials for external organization systems.</p>
        </div>

        <div className="form-grid">
          <label htmlFor="org-picker">Organization</label>
          <input
            id="org-picker"
            list="org-picker-options"
            type="text"
            className="field org-select"
            value={orgPickerValue}
            onChange={handleOrgPickerChange}
            placeholder="Search and select organization..."
            disabled={loadingOrgs || rotating}
          />
          <datalist id="org-picker-options">
            {organizationOptions.map((opt) => (
              <option key={opt.id} value={opt.label} />
            ))}
          </datalist>
        </div>

        <div className="actions">
          <button className="btn primary" disabled={!selectedOrgId || rotating} onClick={handleRotateKey}>
            {rotating ? 'Rotating...' : 'Rotate API Key'}
          </button>
          <button className="btn" disabled={!selectedOrgId || loadingConfig} onClick={() => fetchConfig(selectedOrgId)}>
            {loadingConfig ? 'Refreshing...' : 'Refresh Status'}
          </button>
        </div>

        {error && <div className="msg error">{error}</div>}
        {success && <div className="msg success">{success}</div>}

        {newApiKey && (
          <div className="key-box">
            <div className="key-label">One-time API Key (copy now):</div>
            <textarea
              className="key-textarea"
              readOnly
              value={newApiKey}
              onFocus={(e) => e.target.select()}
            />
            <div className="key-actions">
              <button className="btn" onClick={copyApiKey}>Copy API Key</button>
            </div>
          </div>
        )}

        {config && (
          <div className="status-grid">
            <div><strong>Has Key:</strong> {config.has_key ? 'Yes' : 'No'}</div>
            <div><strong>Key Prefix:</strong> {config.api_key_prefix || '-'}</div>
            <div><strong>Status:</strong> {Number(config.status) === 1 ? 'Active' : 'Disabled'}</div>
            <div><strong>Last Used:</strong> {config.last_used_at || '-'}</div>
            <div><strong>Updated At:</strong> {config.updated_at || '-'}</div>
            <div><strong>Usage Header:</strong> <code>X-Integration-Key: &lt;api_key&gt;</code></div>
          </div>
        )}

        <div className="credential-list">
          <div className="list-header">
            <h4>Organizations With API Credentials</h4>
            <div className="search-wrap">
              <input
                type="text"
                value={search}
                className="field search-field"
                placeholder="Search organization..."
                onChange={(e) => setSearch(e.target.value)}
              />
              <button
                className="btn"
                onClick={() => {
                  const p = 1;
                  setPage(p);
                  loadCredentialRows(p, search);
                }}
                disabled={listLoading}
              >
                Search
              </button>
            </div>
          </div>

          <div className="table-wrap">
            <table className="cred-table">
              <thead>
                <tr>
                  <th>Organization</th>
                  <th>Masked Credential</th>
                  <th>Prefix</th>
                  <th>Status</th>
                  <th>Last Used</th>
                </tr>
              </thead>
              <tbody>
                {listLoading ? (
                  <tr><td colSpan="5">Loading...</td></tr>
                ) : credentialRows.length === 0 ? (
                  <tr><td colSpan="5">No credentials found</td></tr>
                ) : credentialRows.map((row) => (
                  <tr key={row.org_id}>
                    <td>{row.org_name}</td>
                    <td><code>{row.credential_masked}</code></td>
                    <td>{row.api_key_prefix}</td>
                    <td>{Number(row.credential_status) === 1 ? 'Active' : 'Disabled'}</td>
                    <td>{row.last_used_at || '-'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className="pager">
            <button
              className="btn"
              disabled={listLoading || (pagination.page || 1) <= 1}
              onClick={() => {
                const next = Math.max(1, (pagination.page || 1) - 1);
                setPage(next);
                loadCredentialRows(next, search);
              }}
            >
              Prev
            </button>
            <span>
              Page {pagination.page || 1} of {Math.max(1, pagination.total_pages || 1)} ({pagination.total || 0} records)
            </span>
            <button
              className="btn"
              disabled={listLoading || (pagination.page || 1) >= (pagination.total_pages || 1)}
              onClick={() => {
                const next = Math.min((pagination.total_pages || 1), (pagination.page || 1) + 1);
                setPage(next);
                loadCredentialRows(next, search);
              }}
            >
              Next
            </button>
          </div>
        </div>
      </div>

      <style jsx>{`
        .integration-card {
          background: #fff;
          border: 1px solid #e5e7eb;
          border-radius: 12px;
          padding: 1rem;
        }
        .card-header h3 {
          margin: 0 0 0.35rem 0;
          display: flex;
          align-items: center;
          gap: 0.5rem;
        }
        .card-header p {
          margin: 0 0 1rem 0;
          color: #6b7280;
        }
        .form-grid {
          display: grid;
          gap: 0.5rem;
        }
        .field {
          padding: 0.65rem 0.75rem;
          border: 1px solid #d1d5db;
          border-radius: 8px;
        }
        .org-select {
          margin-top: 0.55rem;
        }
        .actions {
          display: flex;
          gap: 0.75rem;
          margin-top: 1rem;
          flex-wrap: wrap;
        }
        .btn {
          border: 1px solid #d1d5db;
          background: #fff;
          color: #111827;
          border-radius: 8px;
          padding: 0.6rem 0.85rem;
          cursor: pointer;
          font-weight: 600;
        }
        .btn.primary {
          border-color: #2563eb;
          background: #2563eb;
          color: #fff;
        }
        .btn:disabled {
          opacity: 0.6;
          cursor: not-allowed;
        }
        .msg {
          margin-top: 1rem;
          padding: 0.7rem 0.85rem;
          border-radius: 8px;
        }
        .msg.error {
          background: #fef2f2;
          color: #991b1b;
          border: 1px solid #fecaca;
        }
        .msg.success {
          background: #ecfdf5;
          color: #065f46;
          border: 1px solid #a7f3d0;
        }
        .key-box {
          margin-top: 1rem;
          background: #111827;
          color: #f9fafb;
          border-radius: 8px;
          padding: 0.8rem;
        }
        .key-label {
          font-size: 0.9rem;
          margin-bottom: 0.45rem;
          color: #9ca3af;
        }
        .key-box code {
          white-space: normal;
          word-break: break-all;
        }
        .key-textarea {
          width: 100%;
          min-height: 66px;
          background: #0b1220;
          color: #f9fafb;
          border: 1px solid #374151;
          border-radius: 8px;
          padding: 0.6rem;
          resize: vertical;
          font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, "Liberation Mono", "Courier New", monospace;
          font-size: 0.9rem;
        }
        .key-actions {
          margin-top: 0.55rem;
          display: flex;
          gap: 0.5rem;
        }
        .status-grid {
          margin-top: 1rem;
          display: grid;
          gap: 0.5rem;
          padding: 0.75rem;
          border: 1px solid #e5e7eb;
          border-radius: 8px;
          background: #f9fafb;
        }
        .credential-list {
          margin-top: 1.25rem;
          border-top: 1px solid #e5e7eb;
          padding-top: 1rem;
        }
        .list-header {
          display: flex;
          justify-content: space-between;
          gap: 0.75rem;
          align-items: center;
          flex-wrap: wrap;
        }
        .list-header h4 {
          margin: 0;
        }
        .search-wrap {
          display: flex;
          gap: 0.5rem;
          align-items: center;
        }
        .search-field {
          min-width: 230px;
        }
        .table-wrap {
          margin-top: 0.75rem;
          overflow-x: auto;
        }
        .cred-table {
          width: 100%;
          border-collapse: collapse;
          font-size: 0.92rem;
        }
        .cred-table th, .cred-table td {
          border-bottom: 1px solid #e5e7eb;
          padding: 0.55rem;
          text-align: left;
          vertical-align: top;
        }
        .cred-table thead th {
          background: #f3f4f6;
        }
        .pager {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-top: 0.8rem;
          gap: 0.5rem;
          flex-wrap: wrap;
        }
      `}</style>
    </div>
  );
};

export default IntegrationSettings;
